import axios from "axios";

// -----------------------------------------------------
// API BASE URL
// -----------------------------------------------------
const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL;

// -----------------------------------------------------
// AXIOS INSTANCE
// -----------------------------------------------------
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json"
  }
});

// -----------------------------------------------------
// DEPLOYMENT MODE
// -----------------------------------------------------

const DEPLOYMENT_MODE =
  String(
    process.env
      .VUE_APP_DEPLOYMENT_MODE ??
    ""
  )
    .trim()
    .toLowerCase();


// -----------------------------------------------------
// EDGE TENANT HOSTNAME
// -----------------------------------------------------
//
// When running an Edge build, this contains the original
// Cloud managed domain of the tenant.
//
// Example:
//
// Local browser:
//   nexgbits.edge.test
//
// Tenant identity:
//   nexgbits.com
//
// Therefore:
//
//   X-Tenant: nexgbits.com
//
// The LocalStandalone / Cloud application continues to
// use the actual browser hostname.
// -----------------------------------------------------

const EDGE_TENANT_HOSTNAME =
  String(
    process.env
      .VUE_APP_EDGE_TENANT_HOSTNAME ??
    ""
  )
    .trim()
    .toLowerCase();


// -----------------------------------------------------
// RESOLVE CURRENT HOSTNAME
// -----------------------------------------------------

const getTenantHostname = () => {
  // ---------------------------------------------------
  // EDGE
  // ---------------------------------------------------

  if (
    DEPLOYMENT_MODE ===
    "edge"
  ) {
    if (
      EDGE_TENANT_HOSTNAME
    ) {
      return (
        EDGE_TENANT_HOSTNAME
      );
    }

    console.warn(
      "[API] Edge deployment does not define " +
      "VUE_APP_EDGE_TENANT_HOSTNAME."
    );

    return null;
  }


  // ---------------------------------------------------
  // CLOUD / NORMAL DEPLOYMENT
  // ---------------------------------------------------

  const hostname =
    window.location.hostname
      ?.trim()
      .toLowerCase();

  if (!hostname) {
    return null;
  }


  // ---------------------------------------------------
  // LOCAL DEVELOPMENT
  // ---------------------------------------------------

  if (
    hostname ===
      "localhost" ||
    hostname ===
      "127.0.0.1"
  ) {
    return null;
  }


  return hostname;
};

// -----------------------------------------------------
// REQUEST INTERCEPTOR
// -----------------------------------------------------
api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("accessToken");

    const tenantId =
      localStorage.getItem("TenantId");

    const userId =
      localStorage.getItem("loginId");

    // -------------------------------------------------
    // AUTHORIZATION
    // -------------------------------------------------
    if (token) {
      config.headers.Authorization =
        `Bearer ${token}`;
    }

    // -------------------------------------------------
    // TENANT HOSTNAME
    // -------------------------------------------------
    //
    // IMPORTANT:
    //
    // Send the COMPLETE browser hostname.
    //
    // Examples:
    //
    // nexgbits-hosting.com
    // nexgbits.com
    // lizmindset.health
    // portal.nexgbits-tech-labs.com
    //
    // Do NOT reduce this to only the first label.
    // -------------------------------------------------

    const tenantHostname =
      getTenantHostname();

    if (tenantHostname) {
      config.headers["X-Tenant"] =
        tenantHostname;
    }

    // -------------------------------------------------
    // EXISTING CONTEXT HEADERS
    // -------------------------------------------------
    if (tenantId) {
      config.headers["X-TenantId"] =
        tenantId;
    }

    if (userId) {
      config.headers["X-UserId"] =
        userId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// -----------------------------------------------------
// RESPONSE INTERCEPTOR
// AUTO REFRESH ACCESS TOKEN
// -----------------------------------------------------
api.interceptors.response.use(
  (response) => {
    return response;
  },

  async (error) => {
    const originalRequest =
      error.config;

    // -------------------------------------------------
    // HANDLE EXPIRED ACCESS TOKEN
    // -------------------------------------------------
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry =
        true;

      try {
        const refreshToken =
          localStorage.getItem(
            "refreshToken"
          );

        const userId =
          localStorage.getItem(
            "loginId"
          );

        const accessToken =
          localStorage.getItem(
            "accessToken"
          );

        if (
          !refreshToken ||
          !userId
        ) {
          throw new Error(
            "Missing refresh token or user ID"
          );
        }

        // ---------------------------------------------
        // REFRESH TOKEN
        // ---------------------------------------------
        //
        // Use plain axios here intentionally.
        //
        // This avoids recursively triggering the
        // api response interceptor if refresh itself
        // returns 401.
        // ---------------------------------------------

        const refreshResponse =
          await axios.post(
            `${API_BASE_URL}/Auth/refresh-token`,
            {
              userId,
              refreshToken,
              accessToken
            }
          );

        const newAccessToken =
          refreshResponse.data
            ?.accessToken;

        const newRefreshToken =
          refreshResponse.data
            ?.refreshToken;

        if (!newAccessToken) {
          throw new Error(
            "The token refresh response did not contain an access token."
          );
        }

        // ---------------------------------------------
        // SAVE NEW TOKENS
        // ---------------------------------------------

        localStorage.setItem(
          "accessToken",
          newAccessToken
        );

        if (newRefreshToken) {
          localStorage.setItem(
            "refreshToken",
            newRefreshToken
          );
        }

        // ---------------------------------------------
        // RETRY ORIGINAL REQUEST
        // ---------------------------------------------

        originalRequest.headers =
          originalRequest.headers || {};

        originalRequest.headers.Authorization =
          `Bearer ${newAccessToken}`;

        /*
         * The request interceptor will run again
         * when api(originalRequest) executes.
         *
         * Therefore X-Tenant will also be rebuilt
         * from the current complete hostname.
         */
        return api(
          originalRequest
        );
      } catch (refreshError) {
        console.error(
          "Refresh token failed:",
          refreshError
        );

        // ---------------------------------------------
        // CLEAR AUTH STATE
        // ---------------------------------------------

        localStorage.removeItem(
          "accessToken"
        );

        localStorage.removeItem(
          "refreshToken"
        );

        localStorage.removeItem(
          "loginId"
        );

        /*
         * Do not necessarily remove TenantId here.
         * If your application uses it for other
         * startup/bootstrap behavior, preserving it
         * avoids unnecessary side effects.
         */

        // ---------------------------------------------
        // FORCE LOGIN
        // ---------------------------------------------

        window.location.href =
          "/login";

        return Promise.reject(
          refreshError
        );
      }
    }

    // -------------------------------------------------
    // FINAL ERROR
    // -------------------------------------------------

    return Promise.reject(
      error.response || error
    );
  }
);

export default api;

export const uploadApi =
  api;